<div class="page-container">
  <app-page-header
    title="Campaign Preview"
    subtitle="Review your campaign details before proceeding"
    [showBackButton]="true"
    [navigateOnBack]="false"
    backLabel="Back to Builder"
    (backClick)="goBack()">
  </app-page-header>

  @if (preview) {
    <div class="preview-content">
      <!-- Summary Cards (display only) -->
      <div class="summary-cards" role="group" aria-label="Campaign summary">
        <div class="summary-card">
          <div class="card-icon" aria-hidden="true">
            <mat-icon>campaign</mat-icon>
          </div>
          <div class="card-content">
            <span class="card-label">Total Airings</span>
            <span class="card-value">{{ preview.total_airings }}</span>
          </div>
        </div>

        <div class="summary-card">
          <div class="card-icon" aria-hidden="true">
            <mat-icon>visibility</mat-icon>
          </div>
          <div class="card-content">
            <span class="card-label">Total Impressions</span>
            <span class="card-value">{{ formatNumber(preview.total_impressions) }}</span>
          </div>
        </div>

        <div class="summary-card">
          <div class="card-icon" aria-hidden="true">
            <mat-icon>attach_money</mat-icon>
          </div>
          <div class="card-content">
            <span class="card-label">Total Cost</span>
            <span class="card-value">{{ formatCurrency(preview.total_cost) }}</span>
          </div>
        </div>

        <div class="summary-card">
          <div class="card-icon" aria-hidden="true">
            <mat-icon>trending_up</mat-icon>
          </div>
          <div class="card-content">
            <span class="card-label">Overall CPM</span>
            <span class="card-value">{{ formatCurrency(preview.overall_cpm) }}</span>
          </div>
        </div>
      </div>

      <!-- Campaign Details (display only) -->
      <section class="details-card">
        <div class="card-header">
          <mat-icon aria-hidden="true">info</mat-icon>
          <h2>Campaign Details</h2>
        </div>
        <div class="details-grid">
          <div class="detail-item">
            <mat-icon aria-hidden="true">radio</mat-icon>
            <div>
              <span class="detail-label">Station</span>
              <span class="detail-value">{{ preview.preview_info?.station_name || previewDetails?.station_name }}</span>
            </div>
          </div>

          <div class="detail-item">
            <mat-icon aria-hidden="true">settings_input_antenna</mat-icon>
            <div>
              <span class="detail-label">Call Sign</span>
              <span class="detail-value">{{ preview.preview_info?.station_call || previewDetails?.station_call_sign }}</span>
            </div>
          </div>

          <div class="detail-item">
            <mat-icon aria-hidden="true">schedule</mat-icon>
            <div>
              <span class="detail-label">Spot Length</span>
              <span class="detail-value">{{ preview.preview_info?.spot_length || previewDetails?.spot_length }}s</span>
            </div>
          </div>

          <div class="detail-item">
            <mat-icon aria-hidden="true">access_time</mat-icon>
            <div>
              <span class="detail-label">Time Range</span>
              <span class="detail-value">{{ preview.preview_info?.time_range || previewDetails?.time_range }}</span>
            </div>
          </div>

          <div class="detail-item">
            <mat-icon aria-hidden="true">calendar_today</mat-icon>
            <div>
              <span class="detail-label">Days</span>
              <span class="detail-value">
                {{
                  preview.preview_info?.allowed_days?.join(', ')
                  || formatDays(previewDetails?.days)
                }}
              </span>
            </div>
          </div>

          <div class="detail-item">
            <mat-icon aria-hidden="true">date_range</mat-icon>
            <div>
              <span class="detail-label">Date Range</span>
              <span class="detail-value">{{ formatDateRange(preview.preview_info?.date_range || previewDetails?.date_range) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Scheduled Ads with pagination (fetches next pages from preview API) -->
      @if (scheduledAdsTotal > 0 || pageAds.length > 0) {
        <section class="ads-card">
          <div class="card-header">
            <mat-icon aria-hidden="true">list</mat-icon>
            <div class="card-header-text">
              <h2>Scheduled Ads Preview</h2>
              <p class="card-header-sub">
                {{ scheduledAdsTotal }} scheduled ad{{ scheduledAdsTotal === 1 ? '' : 's' }} in this campaign
              </p>
            </div>
          </div>

          @if (loadingAds) {
            <div class="ads-loading" role="status">
              <mat-spinner diameter="28"></mat-spinner>
              <span>Loading scheduled ads…</span>
            </div>
          } @else {
            <div class="ads-list" role="list">
              @for (ad of pageAds; track trackAd($index, ad); let i = $index) {
                <div class="ad-item" role="listitem">
                  <div class="ad-number" aria-hidden="true">{{ adsStartIndex + i }}</div>
                  <div class="ad-details">
                    <div class="ad-time">
                      <mat-icon aria-hidden="true">access_time</mat-icon>
                      <span>{{ formatReadableDate(ad.scheduledtime) }}</span>
                    </div>
                    <div class="ad-stats">
                      <div class="stat">
                        <mat-icon aria-hidden="true">visibility</mat-icon>
                        <span>{{ formatNumber(ad.impressions) }} impressions</span>
                      </div>
                      <div class="stat">
                        <mat-icon aria-hidden="true">attach_money</mat-icon>
                        <span>{{ formatCurrency(ad.cost) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          }

          <div class="pagination-panel">
            <p class="pagination-summary">
              Showing {{ paginationStart }}–{{ paginationEnd }} of {{ scheduledAdsTotal }} scheduled ads
            </p>
          </div>

          @if (showPagination) {
            <div class="pagination" role="navigation" aria-label="Scheduled ads pagination">
              <button
                type="button"
                class="pagination-btn"
                [class.disabled]="currentPage === 1 || loadingAds"
                [disabled]="currentPage === 1 || loadingAds"
                (click)="goToPage(currentPage - 1)">
                Previous
              </button>
              <div class="pagination-numbers">
                @for (page of getPaginationPages(); track page) {
                  <button
                    type="button"
                    class="pagination-number"
                    [class.active]="page === currentPage"
                    [disabled]="loadingAds"
                    (click)="goToPage(page)"
                    [attr.aria-current]="page === currentPage ? 'page' : null">
                    {{ page }}
                  </button>
                }
              </div>
              <button
                type="button"
                class="pagination-btn"
                [class.disabled]="currentPage === totalPages || loadingAds"
                [disabled]="currentPage === totalPages || loadingAds"
                (click)="goToPage(currentPage + 1)">
                Next
              </button>
            </div>
          }
        </section>
      }

      <!-- Owner Order Terms (from main) -->
      @if (loadingOrderTerms || hasOrderTerms) {
        <section class="order-terms-card">
          <div class="card-header">
            <mat-icon aria-hidden="true">description</mat-icon>
            <h2>Owner Terms</h2>
          </div>

          @if (loadingOrderTerms) {
            <div class="order-terms-loading">
              <mat-spinner diameter="28"></mat-spinner>
              <span>Loading order terms…</span>
            </div>
          } @else if (hasOrderTerms) {
            <p class="order-terms-intro">
              Please review the following terms from the owner before continuing.
            </p>
            <div class="order-terms-body" tabindex="0" role="region" aria-label="Owner order terms">
              {{ orderTerms }}
            </div>
            <div class="order-terms-ack" [class.has-error]="termsValidationError">
              <mat-checkbox
                class="order-terms-checkbox"
                color="primary"
                [checked]="termsAccepted"
                (change)="onTermsAcceptedChange($event.checked)"
                [disabled]="submitting">
                I have read and agree to the owner terms above.
              </mat-checkbox>
              @if (termsValidationError) {
                <div class="order-terms-error" role="alert">
                  You must accept the owner terms before continuing.
                </div>
              }
            </div>
          }
        </section>
      }

      <div class="action-section">
        <button type="button" class="btn-secondary" (click)="goBack()" [disabled]="submitting">
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
          <span class="btn-label">Back to Builder</span>
        </button>

        <div class="action-group">
          <button
            type="button"
            class="btn-secondary"
            (click)="addToCart()"
            [disabled]="submitting || addingToCart || loadingOrderTerms">
            @if (addingToCart) {
              <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
              <span class="btn-label">Adding…</span>
            } @else {
              <mat-icon aria-hidden="true">add_shopping_cart</mat-icon>
              <span class="btn-label">Add to Cart</span>
            }
          </button>

        @if (isAdminOrOwner) {
          <button
            type="button"
            class="btn-primary"
            (click)="createScheduleDirectly()"
            [disabled]="submitting || loadingOrderTerms">
            @if (submitting) {
              <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
              <span class="btn-label">Creating…</span>
            } @else {
              <mat-icon aria-hidden="true">add_circle</mat-icon>
              <span class="btn-label">Create Schedule</span>
            }
          </button>
        } @else {
          <button
            type="button"
            class="btn-primary"
            (click)="initiateUserPaymentFlow()"
            [disabled]="submitting || loadingOrderTerms">
            @if (submitting) {
              <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
              <span class="btn-label">Initializing…</span>
            } @else {
              <mat-icon aria-hidden="true">payment</mat-icon>
              <span class="btn-label">Proceed to Payment</span>
            }
          </button>
        }
        </div>
      </div>
    </div>
  } @else {
    <div class="no-data">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <h2>No Preview Data</h2>
      <p>Please generate a preview from the campaign builder first.</p>
      <button type="button" class="btn-primary" (click)="goBack()">
        <mat-icon aria-hidden="true">arrow_back</mat-icon>
        <span class="btn-label">Go to Campaign Builder</span>
      </button>
    </div>
  }
</div>
